import { useContext, useEffect, useRef, useState } from 'react'
import { Button, InlineAlert } from '../BaseComponents'
import useApi from '../../hooks/useApi'
import { userContext } from '../../context/UserContext'
import { formatDate, getApiErrorMessage, getCSRFToken } from '../../lib/utils'
import { TEXT_LINK_FOCUS_RING } from '../../lib/a11yFocus'

type PendingInvitation = {
	id: string
	companyName: string | null
	invitedAt: string
}

const LINK_CLASSES = `text-link hover:bg-algae-300 ${TEXT_LINK_FOCUS_RING}`

// Lets a signed-in user act on an invitation without the emailed link; the endpoint only lists the caller's own invitations.
function PendingInvitationsBanner() {
	const api = useApi()
	const { fetchUserData } = useContext(userContext)
	const [invitations, setInvitations] = useState<PendingInvitation[]>([])
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')
	const [loadError, setLoadError] = useState('')
	const [loadingInvitations, setLoadingInvitations] = useState(false)
	const requestSequence = useRef(0)

	const loadInvitations = async () => {
		const request = ++requestSequence.current
		setLoadingInvitations(true)
		try {
			const body = await api.get('pendingInvitations')
			if (request !== requestSequence.current) return
			const list: PendingInvitation[] = Array.isArray(body?.data) ? body.data : []
			// Oldest sent first, so invitations stack in the order they were sent.
			setInvitations([...list].sort((a, b) => new Date(a.invitedAt).getTime() - new Date(b.invitedAt).getTime()))
			setLoadError('')
		} catch (err) {
			if (request === requestSequence.current) {
				setLoadError(getApiErrorMessage(err, 'Could not load invitations. Please try again.'))
			}
		} finally {
			if (request === requestSequence.current) setLoadingInvitations(false)
		}
	}

	useEffect(() => {
		loadInvitations()
		return () => { requestSequence.current += 1 }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onAccept = async (invitation: PendingInvitation) => {
		setBusy(true)
		setError('')
		try {
			await api.post(`pendingInvitations/${invitation.id}/accept`, { _csrf: await getCSRFToken() })
			await fetchUserData()
			await loadInvitations()
		} catch (err) {
			setError(getApiErrorMessage(err, 'Failed to accept this invitation. Please try again.'))
		} finally {
			setBusy(false)
		}
	}

	const onDecline = async (invitation: PendingInvitation) => {
		setBusy(true)
		setError('')
		try {
			await api.post(`pendingInvitations/${invitation.id}/decline`, { _csrf: await getCSRFToken() })
			await loadInvitations()
		} catch (err) {
			setError(getApiErrorMessage(err, 'Failed to decline this invitation. Please try again.'))
		} finally {
			setBusy(false)
		}
	}

	return (
		<>
			{!loadingInvitations && !loadError && invitations.map((invitation) => (
				<InlineAlert key={invitation.id} type='info' data-testid='pending-invitation-message'>
					<div className='flex items-start justify-between gap-4'>
						<p>
							You&apos;ve been invited to join <b>{invitation.companyName ?? 'an organisation'}</b> on Neoni&reg;. Since you
							already have a Neoni account, you can accept the invitation here - no need to sign up again.{' '}
							<button type='button' disabled={busy} className={LINK_CLASSES} onClick={() => onAccept(invitation)}>
								Accept
							</button>{' '}
							<button type='button' disabled={busy} className={LINK_CLASSES} onClick={() => onDecline(invitation)}>
								Decline
							</button>
						</p>
						{invitation.invitedAt && (
							<span className='text-xs shrink-0' data-testid='pending-invitation-date'>
								{formatDate(invitation.invitedAt)}
							</span>
						)}
					</div>
				</InlineAlert>
			))}
			{loadError && (
				<InlineAlert type='error'>
					<p>{loadError}</p>
					<Button
						type='Primary'
						text={loadingInvitations ? 'Retrying...' : 'Retry loading invitations'}
						disabled={loadingInvitations || busy}
						onClick={loadInvitations}
					/>
				</InlineAlert>
			)}
			{error && <InlineAlert type='error'>{error}</InlineAlert>}
		</>
	)
}

export default PendingInvitationsBanner
